import { LOON_HULL, LOON_BILL } from './LoonMark'

/* The hero illustration: the loon resting on the page's waterline with a
   broken reflection beneath it. The viewBox extends 40 units below the
   bird's baseline (y=96) for the reflection, and the CSS pulls the svg
   down by that same fraction (40/240 of the width) so the baseline lands
   exactly on the full-width waterline divider that follows the hero grid.

   Field marks that identify a common loon: the striped necklace collar,
   the white breast at the waterline, the checkerboard saddle, the red eye
   (the one color on the page outside the palette — deliberately). */

const NECKLACE = [
  'M52.5 44 Q51.6 48.5 52.9 53',
  'M58.2 44.6 Q57.3 49.1 58.6 53.6',
  'M63.9 45.2 Q63 49.7 64.3 54.2',
  'M69.6 45.8 Q68.7 50.3 70 54.8',
  'M75.3 46.4 Q74.4 50.9 75.7 55.4',
  'M81 47 Q80.1 51.5 81.4 56',
  'M86.7 47.6 Q85.8 52.1 87.1 56.6',
]

const BREAST =
  'M46.5 70 C47.4 80 50 89 56 96 L86 96 C83.5 88 81 80 77 74.5 C73.5 69.5 68 67 62 67 C56 67 50 67.8 46.5 70 Z'

/* Checkerboard saddle: [x, y, angle]. Each check is rotated to the local
   tangent of the back so the pattern lies along the body like feathers. */
const CHECKS: Array<[number, number, number]> = [
  [104, 55.8, -9.6], [111, 54.6, -9.6], [118, 53.4, -9.6], [125, 53, -1.4],
  [132, 52.8, -1.4], [139, 52.6, -1.4], [146, 52.6, 6.8], [153, 53.5, 6.8],
  [160, 54.3, 6.8], [167, 55.1, 6.8], [174, 56.9, 18.8], [181, 59.2, 18.8],
  [188, 61.6, 18.8], [195, 64, 18.8],
  [109.5, 59.4, -9.6], [116.5, 58.2, -9.6], [123.5, 57.5, -1.4],
  [130.5, 57.3, -1.4], [137.5, 57.2, -1.4], [144.5, 57, -1.4],
  [151.5, 57.8, 6.8], [158.5, 58.6, 6.8], [165.5, 59.5, 6.8],
  [172.5, 60.9, 18.8], [179.5, 63.2, 18.8], [186.5, 65.6, 18.8],
  [193.5, 68, 18.8], [200.5, 72.5, 35.9],
  [117.5, 62.5, -9.6], [124.5, 62, -1.4], [131.5, 61.8, -1.4],
  [138.5, 61.7, -1.4], [145.5, 61.6, 6.8], [152.5, 62.4, 6.8],
  [159.5, 63.2, 6.8], [166.5, 64.1, 6.8], [173.5, 65.7, 18.8],
  [180.5, 68.1, 18.8], [187.5, 70.5, 18.8], [194.5, 72.8, 18.8],
  [133, 66.3, -1.4], [140, 66.1, -1.4], [147, 66.2, 6.8], [154, 67.1, 6.8],
  [161, 67.9, 6.8], [168, 68.8, 6.8], [175, 70.7, 18.8], [182, 73.1, 18.8],
  [189, 75.5, 18.8],
]

const PAPER = 'var(--paper, #f6f2e9)'

export function LoonScene({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 240 136"
      className={className}
      role="img"
      aria-label="Illustration of a common loon resting on lake water"
      fill="currentColor"
    >
      <defs>
        <path id="loon-body" d={LOON_HULL} />
      </defs>

      {/* reflection: the body (bill omitted — it just adds clutter at this
          squash) mirrored below the waterline, cut into bands by
          paper-colored streaks */}
      <use
        href="#loon-body"
        transform="translate(0 139.2) scale(1 -0.45)"
        opacity="0.11"
      />
      <g fill={PAPER}>
        <rect x="30" y="103" width="200" height="2.6" />
        <rect x="55" y="111" width="180" height="3.2" />
        <rect x="40" y="120" width="190" height="4" />
      </g>
      <g stroke="currentColor" strokeWidth="1" opacity="0.35">
        <line x1="14" y1="104" x2="44" y2="104" />
        <line x1="196" y1="108" x2="232" y2="108" />
      </g>

      <use href="#loon-body" />
      <path d={LOON_BILL} />
      <path d={BREAST} fill={PAPER} />
      <g fill="none" stroke={PAPER} strokeWidth="1.9" strokeLinecap="round">
        {NECKLACE.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      <g fill={PAPER}>
        {CHECKS.map(([x, y, angle]) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="4"
            height="2.8"
            rx="0.7"
            transform={`rotate(${angle} ${x + 2} ${y + 1.4})`}
          />
        ))}
      </g>
      <circle cx="56" cy="26.5" r="2.1" fill="#8f3a2a" />
    </svg>
  )
}
